/**
 * @fileoverview One bed open, at /beds/:id (D56): the bed's own card at the
 * page's width, with the way back to every bed (owner, 2026-10-16: the open
 * bed is not a different, poorer layout). The card is the one the Beds list
 * draws (LocationCard): the name and what the bed is in one line, This week
 * with all of the bed's jobs, its photos, Public and Share, Notes, its plants
 * with their stages and weeks, Add a new plant here, Add plants from seed
 * packets, Lately and This bed's season, and the ⋯ menu (Edit details, a
 * note for every plant, clear the bed).
 *
 * Where: LocationGrid, when the address names a bed (/beds/:id).
 * API: none of its own (the page's beds, brief and marks; the card's own).
 * Words (en/es): locations.open.back.
 *
 * @module BedOpen
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { TodoMarks } from '@/features/today/lib/useTodoMarks';
import type { BriefBed, BriefTodo } from '@/shared/api/todayBrief';
import type { Location } from '@/shared/api/locations';
import type { SeasonDiaryRow } from '@/shared/api/season';
import { LocationCard } from './LocationCard';

/** What the open bed draws, and what its controls open. */
interface BedOpenProps {
  /** The bed. */
  location: Location;
  /** Its place in the one to-do list, or null without a brief. */
  bed: BriefBed | null;
  /** The one to-do list, or null without a brief. */
  todo: BriefTodo | null;
  /** Ticking, from the page's useTodoMarks. */
  marks: TodoMarks;
  /** The newest few things that happened here (Lately). */
  lately: SeasonDiaryRow[];
  /** Opens Edit bed on it. */
  onEdit: (location: Location) => void;
  /** Opens one of its plants, full size. */
  onOpenPlant: (plantObjId: number) => void;
  /** Opens the plant form with this bed chosen. */
  onAddPlant: () => void;
  /** Opens "A note for every plant". */
  onNoteAll: () => void;
  /** Opens "Clear the bed for the season". */
  onClearAll: () => void;
}

/**
 * The open bed: the way back, then the bed's card with all its jobs.
 *
 * @param props - BedOpenProps.
 */
export const BedOpen: React.FC<BedOpenProps> = ({ location, bed, todo, marks, lately, onEdit, onOpenPlant, onAddPlant, onNoteAll, onClearAll }) => {
  const { reactTranslate: t } = useTranslation();
  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-3">
      <Link to="/beds" className="-ml-1 inline-flex min-h-9 w-fit items-center gap-0.5 text-sm font-semibold text-primary hover:underline">
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        {t('locations.open.back')}
      </Link>
      <LocationCard
        location={location}
        onEdit={onEdit}
        onOpenPlant={onOpenPlant}
        lately={lately}
        onAddPlant={onAddPlant}
        onNoteAll={onNoteAll}
        onClearAll={onClearAll}
        todo={todo}
        bed={bed}
        marks={marks}
      />
    </div>
  );
};
